import React, { useState, useEffect, useCallback } from 'react';
import { getEscalations, resolveEscalation, isConfigured } from '../lib/api';
import { escalations as mockEsc } from '../data/mockData';

export default function Escalations() {
  const configured = isConfigured();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw = configured ? await getEscalations() : mockEsc;
      setItems(raw.map(i => ({ ...i, _resolved: false })));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [configured]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (item) => {
    setBusy(p => ({ ...p, [item.id]: true }));
    try {
      if (configured) await resolveEscalation(item.id, item.platform);
      setItems(p => p.map(i => i.id === item.id ? { ...i, _resolved: true } : i));
    } catch(e) { alert(e.message); }
    finally { setBusy(p => ({ ...p, [item.id]: false })); }
  };

  const toggle = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const open     = items.filter(i => !i._resolved);
  const resolved = items.filter(i => i._resolved);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Escalation Inbox</div>
          <div className="page-subtitle">Tickets the Concierge couldn't handle. Needs you.</div>
        </div>
        <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
          {!configured && <span style={{fontSize:'11px',color:'var(--text-3)',background:'var(--bg-2)',padding:'4px 10px',borderRadius:'20px',border:'1px solid var(--border)'}}>demo mode</span>}
          <button className="btn btn-edit" onClick={load}>↻ Refresh</button>
          <span style={{fontSize:'12px',color:'var(--text-3)'}}>
            <span style={{color:'var(--red)',fontWeight:'600'}}>{open.filter(i=>i.urgency==='critical').length}</span> critical ·{' '}
            <span style={{color:'var(--yellow)',fontWeight:'600'}}>{open.filter(i=>i.urgency==='high').length}</span> high
          </span>
        </div>
      </div>

      {loading && <div style={{textAlign:'center',padding:'48px',color:'var(--text-3)'}}>Loading...</div>}

      {!loading && open.length === 0 && (
        <div className="card" style={{textAlign:'center',padding:'48px',color:'var(--text-3)'}}>
          <div style={{fontSize:'28px',marginBottom:'8px'}}>📭</div>
          <div style={{fontFamily:'var(--font-display)',fontSize:'14px',fontWeight:'700',color:'var(--text)',marginBottom:'4px'}}>Inbox zero</div>
          <div>Nothing needs your attention right now.</div>
        </div>
      )}

      {open.map(item => (
        <div key={item.id} className="approval-item" style={{marginBottom:'12px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px',flexWrap:'wrap'}}>
            <span className={`urgency-badge ${item.urgency}`}>{item.urgency}</span>
            <span className={`platform-tag ${item.platform}`}>{item.platform}</span>
            <span style={{fontSize:'10px',color:'var(--text-3)'}}>{item.escalation_type || item.type}</span>
            <span style={{marginLeft:'auto',fontSize:'10px',color:'var(--text-3)'}}>
              {item.created_at ? new Date(item.created_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : item.time}
            </span>
          </div>
          <div style={{fontFamily:'var(--font-display)',fontSize:'15px',fontWeight:'700',color:'var(--text)',marginBottom:'4px'}}>{item.subject}</div>
          <div style={{fontSize:'11px',color:'var(--text-3)',marginBottom:'10px'}}>From: {item.from_email || item.from}</div>

          {expanded[item.id] && (
            <div style={{background:'var(--bg-2)',border:'1px solid var(--border)',borderRadius:'6px',padding:'14px',marginBottom:'14px'}}>
              <div style={{fontSize:'10px',color:'var(--text-3)',marginBottom:'6px',letterSpacing:'0.06em',textTransform:'uppercase'}}>Message</div>
              <div style={{fontSize:'12px',color:'var(--text-2)',lineHeight:'1.65'}}>{item.body || item.preview}</div>
              {(item.escalation_type||item.type) === 'partnership' && (
                <div style={{marginTop:'12px',padding:'10px',background:'rgba(0,212,170,0.06)',border:'1px solid rgba(0,212,170,0.15)',borderRadius:'6px'}}>
                  <div style={{fontSize:'10px',color:'var(--accent-m)',marginBottom:'4px',fontWeight:'600'}}>💡 Suggested Action</div>
                  <div style={{fontSize:'11px',color:'var(--text-3)'}}>Reply within 24hrs. High-value opportunity. Draft a call booking response.</div>
                </div>
              )}
              {(item.escalation_type||item.type) === 'billing' && (
                <div style={{marginTop:'12px',padding:'10px',background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:'6px'}}>
                  <div style={{fontSize:'10px',color:'var(--red)',marginBottom:'4px',fontWeight:'600'}}>🚨 Urgent</div>
                  <div style={{fontSize:'11px',color:'var(--text-3)'}}>Check Stripe for the issue. Refund if confirmed. Reply with apology + confirmation.</div>
                </div>
              )}
            </div>
          )}

          <div style={{display:'flex',gap:'8px'}}>
            <button className="btn btn-edit" onClick={()=>toggle(item.id)}>{expanded[item.id]?'Collapse':'Read Message'}</button>
            <button className="btn btn-approve" disabled={busy[item.id]} onClick={()=>resolve(item)}>
              {busy[item.id] ? '⏳...' : '✓ Mark Resolved'}
            </button>
            {(item.escalation_type||item.type)==='partnership' && (
              <button className="btn" style={{background:'var(--accent-m-dim)',color:'var(--accent-m)',borderColor:'rgba(0,212,170,0.2)'}}>📅 Book Call</button>
            )}
          </div>
        </div>
      ))}

      {resolved.length > 0 && (
        <div style={{marginTop:'28px'}}>
          <div className="section-label">Resolved ({resolved.length})</div>
          {resolved.map(item => (
            <div key={item.id} style={{padding:'10px 14px',background:'var(--bg-1)',border:'1px solid var(--border)',borderRadius:'8px',marginBottom:'8px',opacity:0.5,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:'12px',color:'var(--text-2)'}}>{item.subject}</span>
              <span style={{fontSize:'10px',color:'var(--green)'}}>✓ Resolved</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
